import { Box, Pagination } from '@mui/material'
import { useEffect, useState } from 'react'
import ApiClient from '../../backend/ApiClient'
import { IPagination } from '../../definitions/Common'
import { IMembersFilter } from '../../definitions/Members'
import Helper from '../../helpers/Helper'
import TimeHelper from '../../helpers/TimeHelper'
import { useAppSelector } from '../../store/Hooks'
import SmartBadgeFilter from '../badge_filter/SmartBadgeFilter'
import RouteHeader from '../controls/RouteHeader'
import SmartCard from '../controls/cards/SmartCard'
import StaleObserver from '../controls/stale/StaleObserver'
import SmartTable from '../controls/table/SmartTable'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import Tile from '../layout/Tile'
import { useDispatch } from 'react-redux'
import { changeRoute } from '../../store/slices/SessionSlice'
import { SmartTableUtilities } from '../controls/table'

export default function Members() {
    const dispatch = useDispatch()
    const time = useAppSelector(s => s.app.timeSettings ?? TimeHelper.timeSettings())
    const [pagination, setPagination] = useState<IPagination>({ page: 0, batch: 20 })
    const [filter, setFilter] = useState<IMembersFilter>({
        contractIds: [],
        providerIds: []
    })

    useEffect(() => {
        setPagination({ ...pagination, page: 0 })
    }, [filter])

    const { status: statusMembersKPI, response: membersKPI } = useQuery({
        queryKey: buildQueryKey('membersKPI', TimeHelper.stringify(time), Helper.generateKeyOfObject(filter)),
        queryFn: () => ApiClient.getMembersKPI(time, filter),
        cache: true
    })

    const { status, response } = useQuery({
        queryKey: buildQueryKey('members', TimeHelper.stringify(time), pagination.page, pagination.batch, Helper.generateKeyOfObject(filter)),
        queryFn: () => ApiClient.getMembers(time, pagination, filter),
        cache: true
    })

    const isLoading = status === 'loading' || statusMembersKPI === 'loading'
    const totalPage = Math.floor((membersKPI?.totalMembers ?? 1) / pagination.batch)
    const lastPageHaveValues = (membersKPI?.totalMembers ?? 1) % pagination.batch != 0

    // TODO implement if the frontend has permission to see sensitive data (such as member name)
    const havePermissions = false

    return (
        <Tile.Container>
            <RouteHeader primary='Members' />
            <Tile.Row flex={0} spacing={1} style={{ width: '100%' }}>
                <SmartBadgeFilter
                    name='Providers'
                    values={filter.providerIds.map(String)}
                    onApply={x => setFilter(f => ({ ...f, providerIds: x.map(x => Number(x)) }))}
                />
                <SmartBadgeFilter
                    name='Contracts'
                    values={filter.contractIds.map(String)}
                    onApply={x => setFilter(f => ({ ...f, contractIds: x.map(x => Number(x)) }))}
                />
            </Tile.Row>
            <Tile.Column>
                <SmartCard table={true}>
                    <Box sx={{ height: '100%', overflowY: 'auto' }}>
                        <SmartTable
                            columns={[
                                { id: 'uid', label: 'UID' },
                                { id: 'name', label: 'Name', blur: !havePermissions },
                                { id: 'nContracts', label: 'Number of Contract' },
                                { id: 'nProvider', label: 'Number of Provider' }
                            ]}
                            isLoading={isLoading}
                            hidePagination
                            sticky
                            noCollapse
                            onClickRow={i => dispatch(changeRoute({ route: 'member-details', params: { memberId: response[i].id } }))}
                            datasource={{
                                rows: (response ?? []).map((x, i) =>
                                    SmartTableUtilities.toRow(
                                        [
                                            { value: x?.id },
                                            { value: havePermissions ? x?.name : 'placeholder placeholder' },
                                            { value: x?.nContracts },
                                            { value: x?.nProvider }
                                        ],
                                        i
                                    )
                                )
                            }}
                        />

                    </Box>
                    <Box className='smart-table-pagination'>
                        <Pagination
                            key={pagination.page}
                            count={totalPage + (lastPageHaveValues ? 1 : 0)}
                            defaultPage={(pagination.page + 1)}
                            color='primary'
                            disabled={isLoading}
                            onChange={(_, value) => setPagination(p => {
                                return { batch: p.batch, page: value - 1 }
                            })}
                        />
                    </Box>
                </SmartCard>
            </Tile.Column>
            <StaleObserver status={status} />
        </Tile.Container>
    )
}
